import React, { useState, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import DrawerWrapper, { DrawerFooter } from '../IAP/DrawerWrapper';
import FormTextField from '../IAP/formFields/FormTextField';
import FormTextAreaField from '../IAP/formFields/FormTextAreaField';
import { StylishNewButton } from '../DesignSystems/New/StylishNewButton';
import { DGPTAlertCIR } from '../../types/dgptAlerts';
import DataSources from '../DisasterGPT/DataSources';

type CIREditDrawerProps = {
  show: boolean;
  onHide: () => void;
  onSave: (cir: DGPTAlertCIR) => void;
  cir?: DGPTAlertCIR | null;
};

const CIREditDrawer: React.FC<CIREditDrawerProps> = ({
  show,
  onHide,
  onSave,
  cir,
}) => {
  const [selectedDataSources, setSelectedDataSources] = useState<any[]>([]);

  const form = useForm({
    mode: 'onChange',
    defaultValues: {
      name: '',
      prompt: '',
    },
  });

  const { handleSubmit, watch, reset } = form;
  const watchedName = watch('name');

  // Load existing CIR data when editing
  useEffect(() => {
    if (cir) {
      reset({
        name: cir.name,
        prompt: cir.prompt,
      });
      // Convert data_sources to the format expected by DataSources component
      setSelectedDataSources(
        cir.data_sources?.map((ds) => ({
          source: ds.source,
          description: getDataSourceDescription(ds.source),
          id: ds.id || '',
        })) || []
      );
    } else {
      reset({
        name: '',
        prompt: '',
      });
      setSelectedDataSources([]);
    }
  }, [cir, reset]);

  const getDataSourceDescription = (source: string): string => {
    const descriptions: Record<string, string> = {
      News: 'News Articles',
      Files: 'Files uploaded to Pratus',
      Web: 'Web Scraping',
      'Current Weather': 'Current weather at location',
      'Forecast Weather': 'Forecast weather at location',
      'Power Outages': 'Power outages at location',
      'ERI (Energy Reliability Index)': 'Energy Reliability Index at location',
      'ICS-Active': 'Current ICS data',
      'ICS-Historic': 'Full version history of ICS data',
      'NEO Analysis': 'Noncombatant Evacuation Order Analysis',
    };
    return descriptions[source] || source;
  };

  const handleSave = async (formData: any) => {
    const cirData: DGPTAlertCIR = {
      name: formData.name,
      prompt: formData.prompt,
      data_sources: selectedDataSources.map((ds) => ({
        source: ds.source,
        id: ds.id || '',
      })),
    };

    onSave(cirData);
    onHide();
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleSubmit(handleSave)(e);
  };

  if (!show) return null;

  return (
    <DrawerWrapper toggle={onHide} title={cir ? 'Edit CIR' : 'Add CIR'}>
      <FormProvider {...form}>
        <form
          onSubmit={handleFormSubmit}
          className="d-flex flex-column justify-content-between flex-grow-1"
        >
          <div className="p-4">
            <div className="row">
              <FormTextField
                title="CIR Name"
                fieldName="name"
                rules={{
                  required: { value: true, message: 'CIR name is required' },
                }}
                variant="full"
              />

              <FormTextAreaField
                title="AI Instructions (Optional)"
                fieldName="prompt"
                variant="full"
              />
            </div>

            <div className="mt-4">
              <h6>Data Sources</h6>
              <p className="text-muted mb-3">
                Select the data sources this CIR should analyze for information.
              </p>
              <DataSources
                selections={selectedDataSources}
                setSelections={setSelectedDataSources}
                usingSitrep={false}
              />
            </div>
          </div>

          <DrawerFooter>
            <div className="button-group">
              <StylishNewButton
                className="button--secondary button--reverse"
                type="button"
                onClick={onHide}
              >
                Cancel
              </StylishNewButton>
              <StylishNewButton
                className="button--primary"
                type="submit"
                disabled={!watchedName || selectedDataSources.length === 0}
              >
                {cir ? 'Update' : 'Create'} CIR
              </StylishNewButton>
            </div>
          </DrawerFooter>
        </form>
      </FormProvider>
    </DrawerWrapper>
  );
};

export default CIREditDrawer;
